import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { useEffect, useRef, useState } from 'react';

interface PdfPageProps {
  document: PDFDocumentProxy;
  /** 1-based, as pdf.js numbers them. */
  pageNumber: number;
  /** CSS pixels available to the page. The page is scaled to fill this, which
   *  is what keeps a phone showing a whole page width instead of the top-left
   *  corner of one at native size. */
  width: number;
  /** Multiplies the fit-to-width scale, so 1 is "the page fits the screen". */
  zoom: number;
}

/** Rendering at device resolution is what makes small print legible on a
 *  phone, but a 3x buffer of a full page is large enough to be refused on an
 *  older device — past 2x the gain is not worth the allocation. */
const maxPixelRatio = 2;

/**
 * One page of a PDF, drawn to a canvas.
 *
 * Sized by fit-to-width rather than by pdf.js's native scale: a PDF page is
 * described in points at print size, so rendering one at scale 1 on a 360px
 * phone shows a fraction of it and looks like the viewer is stuck zoomed in.
 * Fitting the page to the space available is the whole reason this renders the
 * document itself instead of framing it (§4).
 *
 * Each page renders independently and reports its own aspect ratio, so the
 * scroller reserves the right height before the canvas is painted and the list
 * does not jump as pages arrive.
 */
export const PdfPage = ({ document, pageNumber, width, zoom }: PdfPageProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  useEffect(() => {
    if (width === 0) return;

    let task: RenderTask | undefined;
    let isCancelled = false;

    const render = async () => {
      const page = await document.getPage(pageNumber);
      const canvas = canvasRef.current;

      if (isCancelled || canvas === null) return;

      const unscaled = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: (width * zoom) / unscaled.width });

      setAspectRatio(unscaled.width / unscaled.height);

      const context = canvas.getContext('2d');
      if (context === null) return;

      // The canvas is painted at device resolution and displayed at CSS size:
      // a 1:1 canvas on a 3x phone screen renders type that looks soft next to
      // the app's own text.
      const pixelRatio = Math.min(window.devicePixelRatio, maxPixelRatio);
      canvas.width = Math.floor(viewport.width * pixelRatio);
      canvas.height = Math.floor(viewport.height * pixelRatio);
      canvas.style.width = `${String(Math.floor(viewport.width))}px`;
      canvas.style.height = `${String(Math.floor(viewport.height))}px`;

      task = page.render({
        canvas,
        canvasContext: context,
        viewport,
        transform: [pixelRatio, 0, 0, pixelRatio, 0, 0],
      });

      try {
        await task.promise;
      } catch {
        // A cancelled render is the width or zoom changing mid-paint, which the
        // next effect run is already redrawing. Nothing to report.
      }
    };

    void render();

    return () => {
      isCancelled = true;
      task?.cancel();
    };
  }, [document, pageNumber, width, zoom]);

  return (
    <div
      className="mx-auto bg-white shadow-sm ring-1 ring-black/5"
      // Holds the page's height before its canvas exists, so arriving pages
      // never shove the one being read off the screen.
      style={aspectRatio === null ? { aspectRatio: 1 / 1.414, width } : undefined}
    >
      <canvas ref={canvasRef} aria-label={`Page ${String(pageNumber)}`} role="img" />
    </div>
  );
};
